import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs/internal/Subscription';
import { PaginatorComponent } from 'src/app/components/paginator/paginator.component';
import { FilterForm } from 'src/app/interfaces/groups-filters/groups-filters.interface';
import { GroupMemberInterface } from 'src/app/interfaces/groups/group.interface';
import { GroupsService } from 'src/app/services/groups/groups.service';
import {
  FilterService,
  FormManagementService,
  LoadItemsEvent,
  NamedId,
  SearchPaginationService,
  SelectionService,
} from 'src/app/services/marketing-campaigns/marketing-campaigns-group/marketing-campaign-groups-ids-form';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { atLeastOneChecked } from '../../modals/marketing-campaign-create/marketing-campaign-create.component';
import { BaseGroupForm } from '../marketing-campaign-group-base-form/marketing-campaign-group-base-form';
// Import child components
import {
  SearchPaginationComponent
} from './components';

@Component({
  selector: 'app-marketing-campaign-group-ids-form',
  templateUrl: './marketing-campaign-group-ids-form.component.html',
  styleUrls: ['./marketing-campaign-group-ids-form.component.scss'],
})
export class MarketingCampaignGroupIdsFormComponent
  extends BaseGroupForm
  implements OnChanges, OnInit
{
  @Input() items: NamedId[] = [];
  @Input() itemCount = 0;
  @Input() selectedItemsCount = 0;
  @Input() label: string = 'items';
  @Input()
  set selectedIds(value: Set<string>) {
    this._selectedIds = value;
    if (this.form && this.items.length > 0) {
      this.updateFormControls();
    }
  }
  @Output() loadItems: EventEmitter<LoadItemsEvent> = new EventEmitter<LoadItemsEvent>();
  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;
  @ViewChild(SearchPaginationComponent) searchPaginationComponent!: SearchPaginationComponent;
  public GroupInterfaces = GroupMemberInterface;

  private selectionSubscriptions: Subscription[] = [];
  private valueChangesDisposable: Subscription | undefined;
  private _selectedIds: Set<string> = new Set<string>();

  protected selectAllState: FormControl<boolean | null>;
  protected errorMessage: string | null = null;
  protected reload = false;
  protected searchForm: FormControl;
  protected filtersArray: FormArray<FormGroup<FilterForm>>;

  constructor(
    protected fb: FormBuilder,
    protected spinnerService: SpinnerService,
    protected snackBar: MatSnackBar,
    protected groupsService: GroupsService,
    private filterService: FilterService,
    private selectionService: SelectionService,
    private searchPaginationService: SearchPaginationService,
    private formManagementService: FormManagementService,
  ) {
    super(fb, spinnerService, snackBar, groupsService);
    this.searchForm = this.formManagementService.createSearchControl();
    this.selectAllState = this.formManagementService.createSelectAllControl();
    this.filtersArray = this.fb.array<FormGroup<FilterForm>>([]);
  }

  get selectedIds(): Set<string> {
    return this._selectedIds;
  }

  get filters(): FormArray<FormGroup<FilterForm>> {
    return this.filtersArray as FormArray<FormGroup<FilterForm>>;
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // Setup search functionality using the service
    this.searchPaginationService.setupSearchControl(this.searchForm, (search: string) => {
      this.loadItems.emit(this.searchPaginationService.createSearchEvent(search));
      this.searchPaginationComponent?.resetPagination();
    });

    this.memberChangeCompleted.subscribe(() => {
      this.loadItems.emit();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // TODO: this is strange, but component behavior demands something like this
    this.valueChangesDisposable?.unsubscribe();
    if (changes.form && changes.form.currentValue) {
      this.valueChangesDisposable = this.formManagementService.setupFormValidation(
        this.form,
        this.label,
        (errorMessage: string | null) => {
          this.errorMessage = errorMessage;
        },
      );
    }
    if (changes['groupInterface'] && this.groupInterface) {
      this.filterService.clearFilters(this.filtersArray);
    }
    if (changes['items'] && this.items.length > 0 && this.form) {
      this.updateFormControls();
      this.updateSelectAllState();
    }
  }

  public addFilter(): void {
    this.filterService.addFilter(this.filters, () => this.applyFilters());
  }

  public removeFilter(index: number): void {
    this.filterService.removeFilter(this.filters, index);
    this.applyFilters();
  }

  private applyFilters(): void {
    const validFilters = this.filterService.getValidFilters(this.filters);
    this.selectedIds.clear();
    this.loadItems.emit(this.searchPaginationService.createFiltersEvent(validFilters));
    this.searchPaginationComponent?.resetPagination();
  }

  protected pageEvent(event: any): void {
    const paginationEvent = this.searchPaginationService.createPaginationEvent(
      event.pageSize,
      event.pageIndex,
    );
    this.loadItems.emit(paginationEvent);
  }

  protected updateFormControls(): void {
    const formArray = this.selectionService.createItemFormControls(this.items, this.selectedIds);
    this.form.setControl('items', formArray);
    this.form.updateValueAndValidity();
    this.data ? this.trackAndUpdate() : this.trackSelection();
  }

  protected getDiffIndices(
    prev: any[],
    curr: any[],
  ): { value: boolean; idx?: number; email?: string } | null {
    return this.selectionService.getDiffIndices(prev, curr);
  }

  protected getMemberInfo(diff: { value: boolean; idx?: number; email?: string }): {
    id: string;
    name: string;
  } {
    return this.selectionService.getMemberInfo(diff, this.items);
  }

  protected async addFormItemsControl(): Promise<void> {
    this.form.removeControl('emailAddresses');

    const formArray = this.fb.array(
      this.items.map((item) => new FormControl(this.selectedIds.has(item.id))),
      atLeastOneChecked(),
    );

    this.form.addControl('items', formArray);
  }

  protected trackSelection(): void {
    this.selectionService.unsubscribeSelections(this.selectionSubscriptions);

    const formArray = this.form.get('items') as FormArray;
    this.selectionSubscriptions = this.selectionService.trackSelectionChanges(
      formArray,
      this.items,
      this.selectedIds,
      (selectedIds: Set<string>) => {
        this.selectedItemsCount = selectedIds.size;
        this.loadItems.emit();
        this.updateSelectAllState();
      },
    );
  }

  protected async handleGroupInterfaceChange(): Promise<void> {
    await this.addFormItemsControl();
    this.loadItems.emit();
  }

  protected registerFormListeners(): void {
    this.form.valueChanges.subscribe(() => {
      const rawData = this.formManagementService.getFormDataWithIds(this.form, this.selectedIds);
      this.canSave = this.formManagementService.canSaveForm(this.form);
      this.formManagementService.updateFormSnapshot(
        this.formSnapshots,
        this.groupInterface,
        rawData,
      );
    });
  }

  protected revertFormState(previousValues: any[]): void {
    this.loadItems.emit();
  }

  protected async save(): Promise<void> {
    try {
      const currentInterface: Exclude<GroupMemberInterface, 'emailAddress'> = this
        .groupInterface as Exclude<GroupMemberInterface, 'emailAddress'>;
      const formData = this.formManagementService.getFormSnapshot(
        this.formSnapshots,
        currentInterface,
      );

      const payload = {
        name: formData.name,
        description: formData.description,
      };

      const idsKey = {
        [GroupMemberInterface.Team]: 'teamIds',
        [GroupMemberInterface.Company]: 'companyIds',
        [GroupMemberInterface.Brand]: 'brandIds',
        [GroupMemberInterface.ExistingUser]: 'existingUsersIds',
      }[currentInterface];

      await this.groupsService.createGroup(
        { ...payload, [idsKey]: formData.ids },
        currentInterface,
      );

      this.saved.emit({ data: formData });
    } catch (error) {
      console.error(error);
      this.snackBar.open('Error while creating a Group. Try again in a few minutes.');
    }
  }

  public onToggleSelectAll(): void {
    const formArray = this.form.get('items') as FormArray;
    this.selectionService.toggleSelectAll(formArray, this.items, this.selectedIds);
    this.updateSelectAllState();
  }

  private updateSelectAllState(): void {
    const formArray = this.form.get('items') as FormArray;
    this.selectionService.updateSelectAllControl(this.selectAllState, formArray);
  }
}
